import { callServer, mountAppShell, type AppConfig, type SessionUser } from './index';

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleAccounts {
  id: {
    initialize(options: {
      client_id: string;
      callback: (response: GoogleCredentialResponse) => void;
      hd?: string;
      ux_mode?: 'popup' | 'redirect';
    }): void;
    renderButton(
      parent: HTMLElement,
      options: {
        type: 'standard';
        theme: 'outline';
        size: 'large';
        shape: 'rectangular';
        text: 'signin_with';
        logo_alignment: 'left';
        width: number;
        locale: string;
      }
    ): void;
    disableAutoSelect(): void;
  };
}

declare global {
  interface Window {
    google?: { accounts: GoogleAccounts };
  }
}

let googleScriptPromise: Promise<void> | undefined;

function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client?hl=ms';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Identity Services tidak dapat dimuatkan.'));
    document.head.append(script);
  });

  return googleScriptPromise;
}

async function requestSession(): Promise<SessionUser | null> {
  const response = await fetch('/api/auth/session', {
    credentials: 'include',
    headers: { Accept: 'application/json' }
  });

  if (response.status === 401 || response.status === 404) return null;
  if (!response.ok) throw new Error('Status sesi tidak dapat disemak.');
  const data = (await response.json()) as { user: SessionUser };
  return data.user;
}

async function exchangeCredential(credential: string): Promise<SessionUser> {
  const response = await fetch('/api/auth/google', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'eDICE-PWA'
    },
    body: JSON.stringify({ credential })
  });

  const data = (await response.json()) as { user?: SessionUser; error?: string };
  if (!response.ok || !data.user) {
    throw new Error(data.error || 'Login Google tidak berjaya.');
  }
  return data.user;
}

async function startDevSession(personaId: string): Promise<SessionUser> {
  const response = await fetch('/api/auth/dev', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'eDICE-PWA'
    },
    body: JSON.stringify({ personaId })
  });
  const data = (await response.json()) as { user?: SessionUser; error?: string };
  if (!response.ok || !data.user) throw new Error(data.error || 'Sesi simulasi tidak berjaya.');
  return data.user;
}

async function logout(): Promise<void> {
  await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Requested-With': 'eDICE-PWA' }
  });
  window.google?.accounts.id.disableAutoSelect();
}

function renderLogin(
  root: HTMLElement,
  config: AppConfig,
  clientId: string,
  allowedDomain: string
): void {
  document.title = `Log masuk · ${config.appName}`;
  root.innerHTML = `
    <main class="login-page">
      <section class="login-panel" aria-labelledby="login-heading">
        <span class="login-brand">${config.shortName}</span>
        <p class="eyebrow">BPF Negeri Perak · ${config.environment.toUpperCase()}</p>
        <h1 id="login-heading">Log masuk ke ${config.appName}</h1>
        <p>Gunakan akaun Google Workspace <strong>@${allowedDomain}</strong>. Akses modul masih tertakluk kepada peranan dan kelulusan pentadbir.</p>
        <div id="google-signin-button" class="google-signin"></div>
        <p class="login-status" id="login-status" role="status"></p>
        <small>Data sensitif tidak disimpan pada peranti untuk kegunaan luar talian.</small>
      </section>
    </main>
  `;

  const status = root.querySelector<HTMLElement>('#login-status');
  const button = root.querySelector<HTMLElement>('#google-signin-button');
  if (!status || !button) return;

  loadGoogleIdentityServices()
    .then(() => {
      if (!window.google) throw new Error('Google Identity Services tidak tersedia.');
      window.google.accounts.id.initialize({
        client_id: clientId,
        hd: allowedDomain,
        ux_mode: 'popup',
        callback: (response) => {
          status.textContent = 'Mengesahkan akaun…';
          exchangeCredential(response.credential)
            .then((user) => renderAuthenticated(root, config, user))
            .catch((error: unknown) => {
              status.textContent = error instanceof Error ? error.message : 'Login tidak berjaya.';
            });
        }
      });
      window.google.accounts.id.renderButton(button, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text: 'signin_with',
        logo_alignment: 'left',
        width: 320,
        locale: 'ms'
      });
    })
    .catch((error: unknown) => {
      status.textContent = error instanceof Error ? error.message : 'Butang Google tidak dapat dimuatkan.';
    });
}

function renderConfigurationPending(root: HTMLElement, config: AppConfig): void {
  root.innerHTML = `
    <main class="login-page">
      <section class="login-panel" aria-labelledby="login-heading">
        <span class="login-brand">${config.shortName}</span>
        <p class="eyebrow">OAuth DEV</p>
        <h1 id="login-heading">Konfigurasi login belum lengkap</h1>
        <p>Tambahkan <code>VITE_GOOGLE_CLIENT_ID</code> pada environment Cloudflare Pages selepas OAuth Client DEV diwujudkan.</p>
        <small>Tiada akses aplikasi diberikan sehingga konfigurasi backend dan domain disahkan.</small>
      </section>
    </main>
  `;
}

function renderMockLogin(root: HTMLElement, config: AppConfig): void {
  const personas = [
    { id: 'admin', label: 'Pentadbir Sistem', scope: 'Semua modul DEV' },
    { id: 'bpf-perak', label: 'BPF Perak', scope: 'Skop negeri dan OSC' },
    { id: 'pkd', label: 'Fasiliti PKD', scope: 'PKD dan fasiliti berkaitan' },
    { id: 'hospital', label: 'Fasiliti Hospital', scope: 'Hospital sendiri' }
  ];
  document.title = `Simulasi DEV · ${config.appName}`;
  root.innerHTML = `
    <main class="login-page">
      <section class="login-panel login-panel-wide" aria-labelledby="login-heading">
        <span class="login-brand">${config.shortName}</span>
        <p class="eyebrow">Simulasi akses DEV</p>
        <h1 id="login-heading">Pilih persona ujian</h1>
        <p>Simulator ini bukan login sebenar. Ia hanya untuk data dummy dan akan digantikan dengan Google OAuth organisasi.</p>
        <div class="persona-grid">
          ${personas.map((persona) => `
            <button class="persona-button" type="button" data-persona="${persona.id}">
              <strong>${persona.label}</strong>
              <span>${persona.scope}</span>
            </button>`).join('')}
        </div>
        <p class="login-status" id="login-status" role="status"></p>
        <small>DEV sahaja · Tiada data sebenar · Semua tindakan boleh direkodkan untuk audit.</small>
      </section>
    </main>
  `;

  const status = root.querySelector<HTMLElement>('#login-status');
  root.querySelectorAll<HTMLButtonElement>('[data-persona]').forEach((button) => {
    button.addEventListener('click', () => {
      if (status) status.textContent = 'Membuka sesi simulasi…';
      root.querySelectorAll<HTMLButtonElement>('[data-persona]').forEach((item) => {
        item.disabled = true;
      });
      startDevSession(button.dataset.persona || '')
        .then((user) => renderAuthenticated(root, config, user))
        .catch((error: unknown) => {
          if (status) status.textContent = error instanceof Error ? error.message : 'Sesi tidak berjaya.';
          root.querySelectorAll<HTMLButtonElement>('[data-persona]').forEach((item) => {
            item.disabled = false;
          });
        });
    });
  });
}

function renderAuthenticated(root: HTMLElement, config: AppConfig, user: SessionUser): void {
  mountAppShell(root, config, user);
  const backendStatus = root.querySelector<HTMLElement>('#backend-status');
  callServer<{ ok: boolean; data?: { spreadsheet_connected?: boolean } }>(config, 'system.ping')
    .then((result) => {
      if (backendStatus) {
        backendStatus.textContent = result.ok && result.data?.spreadsheet_connected
          ? 'GAS DEV & Google Sheet dummy bersambung'
          : 'Backend DEV tersedia; database belum disahkan';
      }
    })
    .catch(() => {
      if (backendStatus) backendStatus.textContent = 'Sambungan backend DEV belum dikonfigurasi';
    });
  root.querySelector<HTMLButtonElement>('#logout-button')?.addEventListener('click', () => {
    logout().finally(() => window.location.reload());
  });
}

export async function bootstrapAuthenticatedApp(
  root: HTMLElement,
  config: AppConfig,
  options: { clientId: string; allowedDomain: string; authMode: 'google' | 'mock' }
): Promise<void> {
  if (options.authMode === 'google' && !options.clientId) {
    renderConfigurationPending(root, config);
    return;
  }

  try {
    const user = await requestSession();
    if (user) {
      renderAuthenticated(root, config, user);
      return;
    }
  } catch {
    // A missing session during initial DEV setup falls through to the login gate.
  }

  if (options.authMode === 'mock') {
    renderMockLogin(root, config);
  } else {
    renderLogin(root, config, options.clientId, options.allowedDomain);
  }
}
